import React, {useMemo} from 'react'
import {AspectRatio, Box, Flex, Img, Text, useMultiStyleConfig} from '@chakra-ui/react'
import {FormattedMessage} from 'react-intl'
import PropTypes from 'prop-types'
import {findImageGroupBy} from '@salesforce/retail-react-app/app/utils/image-groups-utils'
import {useDerivedProduct} from '@salesforce/retail-react-app/app/hooks'
import NotifyMeButton from '../../../../components/notify-me-button'

const SMALL = 'small'

const StickyMenu = ({
    product = {},
    renderActionButtons = () => [],
    awardsRef,
    benefitsRef,
    keyFeaturesRef,
    specificationsRef,
    similarRef,
    accessoriesRef,
    isHidden,
}) => {
    const styles = useMultiStyleConfig('StickyMenu')

    const {variationParams, variationAttributes} = useDerivedProduct(product, false, false)

    const color = variationAttributes.find((el) => el.id === 'color')?.selectedValue.name

    const thumbnailImageGroup = useMemo(
        () =>
            product?.imageGroups &&
            findImageGroupBy(product?.imageGroups, {
                viewType: SMALL,
                selectedVariationAttributes: variationParams,
            }),
        [variationParams],
    )
    const thumbnailImages = thumbnailImageGroup?.images || []
    const image = thumbnailImages && thumbnailImages[0] && thumbnailImages[0]

    const actionButtons = renderActionButtons()
    const addToCartButton = actionButtons.find((button) => button.key === 'cart-button')

    const scrollToSection = (ref) => {
        if (ref.current)
            ref.current.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
            })
    }
    const notifyMe = product?.inventory?.stockLevel === 0

    return (
        <Box data-testid="sticky-menu" {...styles.stickyBar}>
            <Flex layerStyle="full-width" {...styles.stickyBarContent}>
                <Box hideBelow="md">
                    <Flex {...styles.productContainer}>
                        {image && (
                            <AspectRatio {...styles.productImage}>
                                <Img alt={image.alt} src={image.disBaseLink || image.link} />
                            </AspectRatio>
                        )}
                        <Box margin="auto">
                            <Text {...styles.productText}>{`${
                                product?.c_variantIdentifier ||
                                product?.name ||
                                product?.productName
                            } ${color ?? ''}`}</Text>
                            <Text {...styles.productTextCode}>
                                {product?.c_subModelIdentifier || product?.id}
                            </Text>
                        </Box>
                    </Flex>
                </Box>
                <Box hideBelow="lg" {...styles.sectionButtonGroup}>
                    {awardsRef?.current && (
                        <Text {...styles.sectionButton} onClick={() => scrollToSection(awardsRef)}>
                            <FormattedMessage id="stickyMenu.awards" defaultMessage="Awards" />
                        </Text>
                    )}
                    {benefitsRef?.current && (
                        <Text
                            {...styles.sectionButton}
                            onClick={() => scrollToSection(benefitsRef)}
                        >
                            <FormattedMessage id="stickyMenu.benefits" defaultMessage="Benefits" />
                        </Text>
                    )}
                    {keyFeaturesRef?.current && (
                        <Text
                            {...styles.sectionButton}
                            onClick={() => scrollToSection(keyFeaturesRef)}
                        >
                            <FormattedMessage
                                id="stickyMenu.keyFeatures"
                                defaultMessage="Key Features"
                            />
                        </Text>
                    )}
                    {specificationsRef?.current && (
                        <Text
                            {...styles.sectionButton}
                            onClick={() => scrollToSection(specificationsRef)}
                        >
                            <FormattedMessage
                                id="stickyMenu.specifications"
                                defaultMessage="Specifications"
                            />
                        </Text>
                    )}
                    {similarRef?.current && (
                        <Text {...styles.sectionButton} onClick={() => scrollToSection(similarRef)}>
                            <FormattedMessage id="stickyMenu.similar" defaultMessage="Similar" />
                        </Text>
                    )}
                    {accessoriesRef?.current && (
                        <Text
                            {...styles.sectionButton}
                            onClick={() => scrollToSection(accessoriesRef)}
                        >
                            <FormattedMessage
                                id="stickyMenu.accessories"
                                defaultMessage="Accessories"
                            />
                        </Text>
                    )}
                </Box>
                <Box {...styles.buttonContainer}>
                    {product?.c_skuIsPDPOnly !== 'true' && notifyMe ? (
                        <NotifyMeButton product={product} tabIndex={isHidden ? -1 : undefined} />
                    ) : (
                        addToCartButton
                    )}
                </Box>
            </Flex>
        </Box>
    )
}

StickyMenu.propTypes = {
    product: PropTypes.object,
    renderActionButtons: PropTypes.func,
    awardsRef: PropTypes.object,
    benefitsRef: PropTypes.object,
    keyFeaturesRef: PropTypes.object,
    specificationsRef: PropTypes.object,
    similarRef: PropTypes.object,
    accessoriesRef: PropTypes.object,
    isHidden: PropTypes.bool,
}

export default StickyMenu
